import React, { useMemo } from "react";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { defineMessage, useIntl } from "react-intl";

import EntityType from "@mapmycustomers/shared/enum/EntityType";

import Statistics from "@app/types/Statistics";
import { doesWeekStartFromMonday } from "@app/util/dates";
import { formatDate } from "@app/util/formatters";
import { parseApiDateAsNumber } from "@app/util/parsers";
import { getEntityTypeDisplayName } from "@app/util/ui";

import NoChart from "../../charts/NoChart";

const recordsAddedOverTimeChartTitle = defineMessage({
  id: "reports.overview.chart.recordsAddedOverTime",
  defaultMessage: "Records Added Over Time",
  description: "Records Added Over Time chart title on the Overview page in Reports",
});

const recordToChartItem = ([date, value]: [string, number]) => ({
  name: formatDate(parseInt(date, 10), "PPP"),
  x: parseInt(date, 10),
  y: value,
});

interface Props {
  numberOfDays?: number;
  recordsOverTime: Statistics[];
}

const RecordsOverTimeChart: React.FC<Props> = ({ numberOfDays, recordsOverTime }) => {
  const intl = useIntl();

  const chartData = useMemo(() => {
    const activitiesOverTimeRaw: Record<number, number> = {};
    const companiesOverTimeRaw: Record<number, number> = {};
    const dealsOverTimeRaw: Record<number, number> = {};
    const peopleOverTimeRaw: Record<number, number> = {};

    recordsOverTime
      .filter(({ date }) => date)
      .forEach(({ date, values }) => {
        const timestamp = parseApiDateAsNumber(date!);
        activitiesOverTimeRaw[timestamp] = values.activitiesCreated || 0;
        companiesOverTimeRaw[timestamp] = values.accountsCreated || 0;
        dealsOverTimeRaw[timestamp] = values.dealsCreated || 0;
        peopleOverTimeRaw[timestamp] = values.contactsCreated || 0;
      });

    return {
      activitiesOverTime: Object.entries(activitiesOverTimeRaw).map(recordToChartItem),
      companiesOverTime: Object.entries(companiesOverTimeRaw).map(recordToChartItem),
      dealsOverTime: Object.entries(dealsOverTimeRaw).map(recordToChartItem),
      peopleOverTime: Object.entries(peopleOverTimeRaw).map(recordToChartItem),
    };
  }, [recordsOverTime]);

  const chartDataNewRecordsOverTime: Highcharts.Options = useMemo(
    () => ({
      chart: {
        plotBackgroundColor: undefined,
        plotBorderWidth: undefined,
        plotShadow: false,
        scrollablePlotArea: {
          minWidth: numberOfDays && numberOfDays * 10,
          opacity: 0,
          scrollPositionX: 1,
        },
        zoomType: "x",
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        column: {
          borderWidth: 1,
          dataLabels: {
            enabled: false,
          },
          stacking: "normal",
        },
      },
      series: [
        {
          data: chartData.companiesOverTime,
          name: getEntityTypeDisplayName(intl, EntityType.COMPANY, {
            lowercase: false,
            plural: true,
          }),
          type: "column",
        },
        {
          data: chartData.peopleOverTime,
          name: getEntityTypeDisplayName(intl, EntityType.PERSON, {
            lowercase: false,
            plural: true,
          }),
          type: "column",
        },
        {
          data: chartData.activitiesOverTime,
          name: getEntityTypeDisplayName(intl, EntityType.ACTIVITY, {
            lowercase: false,
            plural: true,
          }),
          type: "column",
        },
        {
          data: chartData.dealsOverTime,
          name: getEntityTypeDisplayName(intl, EntityType.DEAL, { lowercase: false, plural: true }),
          type: "column",
        },
      ],
      time: {
        timezoneOffset: new Date().getTimezoneOffset(),
      },
      title: {
        text: intl.formatMessage(recordsAddedOverTimeChartTitle),
      },
      tooltip: {
        pointFormat: "{series.name}: <b>{point.y}</b>",
      },
      xAxis: {
        labels: {
          formatter: function () {
            return formatDate(this.value as number, "P");
          },
        },
        startOfWeek: doesWeekStartFromMonday() ? 1 : 0,
        tickInterval: 24 * 3600 * 1000,
        type: "datetime",
      },
    }),
    [chartData, intl, numberOfDays]
  );

  return chartData.companiesOverTime.length ||
    chartData.peopleOverTime.length ||
    chartData.dealsOverTime.length ||
    chartData.activitiesOverTime.length ? (
    <HighchartsReact highcharts={Highcharts} options={chartDataNewRecordsOverTime} />
  ) : (
    <NoChart chartTitle={intl.formatMessage(recordsAddedOverTimeChartTitle)} />
  );
};

export default RecordsOverTimeChart;
